import L from 'leaflet';
import PropTypes from 'prop-types';
import { filter, find, path, pathOr, propEq } from 'ramda';
import React, { Component } from 'react';
import { LayerGroup, Marker, Popup } from 'react-leaflet';
import { connect } from 'react-redux';

import { isASL } from '../modules/selectors/permissions';


/**
 * React component.
 */
const TYPE_RED = 1;
const TYPE_GREEN = 2;
const TYPE_BLUE = 0;
const TYPE_GREY = 3;

const MARKER_ICONS = {
  [TYPE_RED]: L.icon({
    iconUrl: 'images/bus-marker-48.png',
    iconRetinaUrl: 'images/bus-marker-48-2x.png',
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  }),
  [TYPE_GREEN]: L.icon({
    iconUrl: 'images/bus-marker-48-green.png',
    iconRetinaUrl: 'images/bus-marker-48-green-2x.png',
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  }),
  [TYPE_BLUE]: L.icon({
    iconUrl: 'images/bus-marker-48-blue.png',
    iconRetinaUrl: 'images/bus-marker-48-blue-2x.png',
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  }),
  [TYPE_GREY]: L.icon({
    iconUrl: 'images/bus-marker-48-grey.png',
    iconRetinaUrl: 'images/bus-marker-48-grey-2x.png',
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  }),
};

class RideFeature extends Component {
  static propTypes = {
    // Own props.
    companyName: PropTypes.string.isRequired,
    driverName: PropTypes.string.isRequired,
    driverPhone: PropTypes.string.isRequired,
    position: PropTypes.array.isRequired,
    type: PropTypes.number.isRequired,
    vehicleName: PropTypes.string.isRequired,
    isASL: PropTypes.bool.isRequired,
    vehicle: PropTypes.shape({
      attributes: PropTypes.shape({
        seats: PropTypes.number.isRequired,
        'standing-places': PropTypes.number.isRequired,
        'bikes-allowed': PropTypes.bool.isRequired,
        'toilet-available': PropTypes.bool.isRequired,
      }).isRequired,
    }),
  };

  shouldComponentUpdate(nextProps, nextState) {
    // Update when the position changes.
    if (this.props.position[0] !== nextProps.position[0]) return true;
    if (this.props.position[1] !== nextProps.position[1]) return true;

    // Update when popup information changes.
    if (this.props.companyName !== nextProps.companyName) return true;
    if (this.props.driverName !== nextProps.driverName) return true;
    if (this.props.driverPhone !== nextProps.driverPhone) return true;
    if (this.props.vehicleName !== nextProps.vehicleName) return true;

    // Update when the type changes.
    if (this.props.type !== nextProps.type) return true;

    return false;
  };

  render() {
    const { companyName, driverName, driverPhone, position, type, vehicleName, isASL, vehicle } = this.props;

    const rideMarkerIcon = MARKER_ICONS[type] || MARKER_ICONS[TYPE_GREY];

    const ba = path(['attributes', 'bikes-allowed'], vehicle);
    const ta = path(['attributes', 'toilet-available'], vehicle);

    const vehicleBikesAllowed = ba === true ? 'Ja' : ba === false ? 'Nee' : 'n.v.t.';
    const vehicleSeats = pathOr('n.v.t.', ['attributes', 'seats'], vehicle);
    const vehicleStandingPlaces = pathOr('n.v.t.', ['attributes', 'standing-places'], vehicle);
    const vehicleToiletAvailable = ta === true ? 'Ja' : ba === false ? 'Nee' : 'n.v.t.';

    return (
      <Marker position={position} icon={rideMarkerIcon}>
        <Popup>
          {isASL ? (
            <span>
              Bus {vehicleName} van {companyName}.<br />
              <em>
                zitplaatsen={vehicleSeats}&nbsp;
                staplaatsen={vehicleStandingPlaces}&nbsp;
                fietsen={vehicleBikesAllowed}&nbsp;
                toilet={vehicleToiletAvailable}&nbsp;
              </em>
            </span>
          ) : (
            <span>
              Chauffeur {driverName} rijdt bus {vehicleName} voor {companyName}. Te bereiken op {driverPhone}.
            </span>
          )}
        </Popup>
      </Marker>
    );
  };
};

class RidesLayer extends Component {
  static propTypes = {
    rides: PropTypes.array.isRequired,
    drivers: PropTypes.array.isRequired,
    vehicles: PropTypes.array.isRequired,
    companies: PropTypes.array.isRequired,
    isASL: PropTypes.bool.isRequired,
  };

  render() {
    const { rides, drivers, vehicles, companies, isASL } = this.props;

    if (rides.length === 0) {
      return null;
    }

    return (
      <LayerGroup>
      {rides.map(ride => {
        const driverId = String(ride.relationships.driver.data.id);
        const vehicleId = String(ride.relationships.vehicle.data.id);
        const companyId = String(ride.attributes['company-id']);

        const driver = find(propEq('id', driverId))(drivers);
        const vehicle = find(propEq('id', vehicleId))(vehicles);
        const company = find(propEq('id', companyId))(companies);

        const driverName = driver ? (
          path(['attributes', 'first-name'], driver) + ' ' +
          path(['attributes', 'last-name'], driver)) :
          'Onbekende chauffeur ' + driverId;
        const driverPhone = driver ? path(['attributes', 'phone'], driver) :
          'Onbekend telefoonnummer';
        const vehicleName = vehicle ? (
          path(['attributes', 'vehicle-number'], vehicle) + ' (' +
          path(['attributes', 'license-plate'], vehicle) + ')') :
          'Onbekend voertuig ' + vehicleId;
        const companyName = company ? path(['attributes', 'name'], company) :
          'Onbekend bedrijf ' + companyId;

        let type = TYPE_GREY;

        if (typeof ride.attributes['distance-depot-start'] !== 'number' &&
            typeof ride.attributes['distance-shuttle-start'] !== 'number' &&
            typeof ride.attributes['distance-shuttle-end'] !== 'number' &&
            typeof ride.attributes['distance-depot-end'] !== 'number')
          type = TYPE_BLUE;
        else if (typeof ride.attributes['distance-depot-start'] === 'number' &&
                 typeof ride.attributes['distance-shuttle-start'] !== 'number' &&
                 typeof ride.attributes['distance-shuttle-end'] !== 'number' &&
                 typeof ride.attributes['distance-depot-end'] !== 'number')
          type = TYPE_RED;
        else if (typeof ride.attributes['distance-depot-start'] === 'number' &&
                 typeof ride.attributes['distance-shuttle-start'] === 'number' &&
                 typeof ride.attributes['distance-shuttle-end'] !== 'number' &&
                 typeof ride.attributes['distance-depot-end'] !== 'number')
          type = TYPE_GREEN;

        return (
          <RideFeature
            key={ride.id}
            position={[ride.attributes.location.coordinates[1], ride.attributes.location.coordinates[0]]}
            companyName={companyName}
            driverName={driverName}
            driverPhone={driverPhone}
            type={type}
            vehicleName={vehicleName}
            isASL={isASL}
            vehicle={vehicle}
          />
        );
      })}
      </LayerGroup>
    );
  };
}


/**
 * Redux requirements.
 */
const selectActiveRides = (state) => {
  const rides = path(['api', 'ride', 'data'], state) || [];

  const isActive = r => {
    const hasEnded = typeof path(['attributes', 'distance-depot-end'], r) === 'number';
    const hasLocation = path(['attributes', 'location', 'type'], r) === 'Point';

    return !hasEnded && hasLocation;
  };

  return filter(isActive)(rides);
};

const mapStateToProps = state => ({
  rides: selectActiveRides(state),
  drivers: path(['api', 'api-user', 'data'], state) || [],
  vehicles: path(['api', 'vehicle', 'data'], state) || [],
  companies: path(['api', 'company', 'data'], state) || [],
  isASL: isASL(state),
});

export default connect(mapStateToProps)(RidesLayer);
